import {
  Component,
  OnInit,
  Input,
  OnChanges,
  SimpleChanges,
  Output,
  EventEmitter,
  ViewChild,
} from "@angular/core";
import { formatMessage } from "devextreme/localization";
import { CalculatorUsFieldItemsService } from "../../../../../services/calculator-us-field-items.service";
import { DxAccordionComponent, DxFormComponent } from "devextreme-angular";
import { UsCalculatorFieldItem } from "../../../../../model/calculator-us-remastered/usCalculatorFieldItem";
import { PaperSpecification } from "../../../../../model/calculator-us-remastered/paperSpecification";
import { PrePressSpecification } from "../../../../../model/calculator-us-remastered/prePressSpecification";
import { SummaryItem } from "../../../../../model/calculator-us-remastered/summaryItem";

export interface SummaryClearEvent {
  type: "clear";
  category: string;
}

export interface SummaryUpdateEvent {
  type: "update";
  items: SummaryItem[];
}

export type SummaryEvent = SummaryUpdateEvent | SummaryClearEvent;

@Component({
  selector: "app-calculator-prepress",
  templateUrl: "./calculator-prepress.component.html",
  styleUrls: ["./calculator-prepress.component.scss"],
})
export class CalculatorPrepressComponent implements OnChanges {
  @Input() selectedPublisherId: string | undefined;
  @Input() selectedClassification: string | undefined;
  @Input() selectedInitialReprint: string;
  @Input() prepressEnabled: boolean = false;
  @Input() showToggleButton: boolean = false;
  @Output() summaryUpdate = new EventEmitter<SummaryItem[]>();
  @Output() togglePrepress = new EventEmitter<void>();
  @Output() onValueChanged = new EventEmitter<PrePressSpecification>();

  @ViewChild("prePressForm") prePressForm: DxFormComponent;
  @ViewChild("accordion") accordion: DxAccordionComponent;

  private _prePressSpecification: PrePressSpecification;
  @Input()
  set prePressSpecification(val: PrePressSpecification) {
    this._prePressSpecification = val;
  }
  get prePressSpecification(): PrePressSpecification {
    return this._prePressSpecification;
  }

  formatMessage = formatMessage;
  collapsed: any = true;

  // Selected values
  private _selectedPrepPrint: UsCalculatorFieldItem | null;
  get selectedPrepPrint(): UsCalculatorFieldItem | null {
    return this._selectedPrepPrint;
  }

  set selectedPrepPrint(value: UsCalculatorFieldItem | null) {
    this._selectedPrepPrint = value;
    this.updateSummary();

    this._prePressSpecification.prepPrint = value?.keyValue;
    this.onValueChanged.emit(this.prePressSpecification);
  }

  private _selectedProofSoft: UsCalculatorFieldItem | null;
  get selectedProofSoft(): UsCalculatorFieldItem | null {
    return this._selectedProofSoft;
  }

  set selectedProofSoft(value: UsCalculatorFieldItem | null) {
    this._selectedProofSoft = value;
    this.updateSummary();

    this._prePressSpecification.proofSoft = value?.keyValue;
    this.onValueChanged.emit(this.prePressSpecification);
  }

  private _selectedComponentPrepPrint: UsCalculatorFieldItem | null;
  get selectedComponentPrepPrint(): UsCalculatorFieldItem | null {
    return this._selectedComponentPrepPrint;
  }

  set selectedComponentPrepPrint(value: UsCalculatorFieldItem | null) {
    this._selectedComponentPrepPrint = value;
    this.updateSummary();

    this._prePressSpecification.componentPrepPrint = value?.keyValue;
    this.onValueChanged.emit(this.prePressSpecification);
  }

  private _selectedComponentProofSoft: UsCalculatorFieldItem | null;
  get selectedComponentProofSoft(): UsCalculatorFieldItem | null {
    return this._selectedComponentProofSoft;
  }

  set selectedComponentProofSoft(value: UsCalculatorFieldItem | null) {
    this._selectedComponentProofSoft = value;
    this.updateSummary();

    this._prePressSpecification.componentProofSoft = value?.keyValue;
    this.onValueChanged.emit(this.prePressSpecification);
  }

  componentProofSoftDataSource: UsCalculatorFieldItem[] = [];
  componentPrepPrintDataSource: UsCalculatorFieldItem[] = [];
  proofSoftDataSource: UsCalculatorFieldItem[] = [];
  prepPrintDataSource: UsCalculatorFieldItem[] = [];

  constructor(
    private calculatorUsFieldItemsService: CalculatorUsFieldItemsService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    // Close accordion when classification changes
    if (changes.selectedClassification) {
      this.resetFieldItems(true);
    }

    if (
      changes.prepressEnabled ||
      (this.prepressEnabled && changes.selectedClassification)
    ) {
      this.resetFieldItems();
      this.loadFieldItems();
    }
  }

  private loadFieldItems(): void {
    if (!this.selectedPublisherId || !this.selectedClassification) {
      return;
    }

    // Verwende optimierte Methode für gesammelte Subkategorie-Abfrage
    this.calculatorUsFieldItemsService
      .getFieldItemsBySubcategory(
        this.selectedPublisherId,
        this.calculatorUsFieldItemsService.getDbClassification(
          this.selectedClassification
        ),
        "PRE_PRESS"
      )
      .subscribe((fieldItems) => {
        // Reset arrays
        this.prepPrintDataSource = [];
        this.proofSoftDataSource = [];
        this.componentPrepPrintDataSource = [];
        this.componentProofSoftDataSource = [];

        // Mapping über fieldItem.inputName wie in der Hauptkomponente
        for (const fieldItem of fieldItems) {
          if (fieldItem.inputName === "PRINT_PREP_PRINT_READ_PDF") {
            this.prepPrintDataSource.push(fieldItem);
            if (fieldItem.isDefault) {
              this._selectedPrepPrint = fieldItem;
            }
          } else if (fieldItem.inputName === "PRINT_PROOF_SOFT_PROOF") {
            this.proofSoftDataSource.push(fieldItem);
            if (fieldItem.isDefault) {
              this._selectedProofSoft = fieldItem;
            }
          } else if (fieldItem.inputName === "COMPONENT_PREP_PRINT_READ_PDF") {
            this.componentPrepPrintDataSource.push(fieldItem);
            if (fieldItem.isDefault) {
              this._selectedComponentPrepPrint = fieldItem;
            }
          } else if (fieldItem.inputName === "COMPONENT_PROOF_SOFT_PROOF") {
            this.componentProofSoftDataSource.push(fieldItem);
            if (fieldItem.isDefault) {
              this._selectedComponentProofSoft = fieldItem;
            }
          }
        }

        this.updateSummary();
      });
  }

  onAccordionClick(e) {
    const scrollPosition =
      window.pageYOffset || document.documentElement.scrollTop;

    this.collapsed = e.component.option("selectedIndex") == -1;

    setTimeout(() => {
      window.scrollTo(0, scrollPosition);
    }, 100);
  }

  onTogglePrepress(): void {
    this.togglePrepress.emit();
    // updateSummary wird durch ngOnChanges aufgerufen, wenn prepressEnabled sich ändert
  }

  // Event handlers for value changes
  prepPrintChanged(event: any): void {
    const selectedValue = event.value;
    this._selectedPrepPrint =
      this.prepPrintDataSource?.find(
        (item: any) => item.keyValue === selectedValue
      ) || null;

    this.updateSummary();
  }

  proofSoftChanged(event: any): void {
    const selectedValue = event.value;
    this._selectedProofSoft =
      this.proofSoftDataSource?.find(
        (item: any) => item.keyValue === selectedValue
      ) || null;

    this.updateSummary();
  }

  componentPrepPrintChanged(event: any): void {
    const selectedValue = event.value;
    this._selectedComponentPrepPrint =
      this.componentPrepPrintDataSource?.find(
        (item: any) => item.keyValue === selectedValue
      ) || null;

    this.updateSummary();
  }

  componentProofSoftChanged(event: any): void {
    const selectedValue = event.value;
    this._selectedComponentProofSoft =
      this.componentProofSoftDataSource?.find(
        (item: any) => item.keyValue === selectedValue
      ) || null;

    this.updateSummary();
  }

  private updateSummary(): void {
    const summaryItems: SummaryItem[] = [];

    if (!this.prepressEnabled) {
      // Prepress deaktiviert - sende Clear-Event
      this.summaryUpdate.emit([]); // Für Rückwärtskompatibilität
      return;
    }

    // Prep Print
    if (this._selectedPrepPrint) {
      const defaultPrepPrint = this.prepPrintDataSource?.find(
        (item: any) => item.isDefault
      );
      summaryItems.push(
        new SummaryItem(
          "prepPrint",
          "Print & Bind - Prep",
          this._selectedPrepPrint.displayValue,
          defaultPrepPrint?.displayValue || "",
          "Prepress",
          undefined
        )
      );
    }

    // Proof Soft
    if (this._selectedProofSoft) {
      const defaultProofSoft = this.proofSoftDataSource?.find(
        (item: any) => item.isDefault
      );
      summaryItems.push(
        new SummaryItem(
          "proofSoft",
          "Print & Bind - Proof",
          this._selectedProofSoft.displayValue,
          defaultProofSoft?.displayValue || "",
          "Prepress",
          undefined
        )
      );
    }

    // Component Prep Print
    if (this._selectedComponentPrepPrint) {
      const defaultComponentPrepPrint = this.componentPrepPrintDataSource?.find(
        (item: any) => item.isDefault
      );
      summaryItems.push(
        new SummaryItem(
          "componentPrepPrint",
          "Component - Prep",
          this._selectedComponentPrepPrint.displayValue,
          defaultComponentPrepPrint?.displayValue || "",
          "Prepress",
          undefined
        )
      );
    }

    // Component Proof Soft
    if (this._selectedComponentProofSoft) {
      const defaultComponentProofSoft = this.componentProofSoftDataSource?.find(
        (item: any) => item.isDefault
      );
      summaryItems.push(
        new SummaryItem(
          "componentProofSoft",
          "Component - Proof",
          this._selectedComponentProofSoft.displayValue,
          defaultComponentProofSoft?.displayValue || "",
          "Prepress",
          undefined
        )
      );
    }

    this.summaryUpdate.emit(summaryItems); // Für Rückwärtskompatibilität
  }

  public resetFieldItems(closeAccordion = false): void {
    // Reset selected values
    this.selectedPrepPrint = null;
    this.selectedProofSoft = null;
    this.selectedComponentPrepPrint = null;
    this.selectedComponentProofSoft = null;

    // Reset field items
    this.componentProofSoftDataSource = [];
    this.componentPrepPrintDataSource = [];
    this.proofSoftDataSource = [];
    this.prepPrintDataSource = [];

    if (this.accordion && closeAccordion)
      this.accordion.instance.collapseItem(0).then();
    this.summaryUpdate.emit([]);
  }
}
